/** The add-ons module's reads and writes of the API (docs/plans/feature-licensing-plan.md): TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPut } from '../core/index.js'
import { useFacility } from '../facilities/index.js'
import { useMe } from '../accounts/index.js'
import type { ActivationResponse, FacilityAddOn } from './types.js'

/** Every add-on's state at a facility, for the Configuration page's Add-ons tab. Null stays idle. */
export function useFacilityAddOns(facilityId: string | null) {
  return useQuery({
    queryKey: ['facility-add-ons', facilityId],
    queryFn: () => apiGet<FacilityAddOn[]>(`/facilities/${encodeURIComponent(facilityId as string)}/add-ons`),
    enabled: facilityId !== null,
  })
}

/** The operator's switch for one facility: on or off, within the client's license. Refreshes every facility read, since each carries `features`. */
export function useSetFacilityAddOn(clientId: string | undefined, facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      apiPut<ActivationResponse>(`/admin/clients/${encodeURIComponent(clientId ?? '')}/facilities/${encodeURIComponent(facilityId)}/add-ons/${encodeURIComponent(key)}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facility-add-ons', facilityId] })
      void queryClient.invalidateQueries({ queryKey: ['facilities'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** What a screen of an add-on needs before it renders: whether the client is licensed at all, and whether the facility has it on. */
export interface FeatureStanding {
  isLoading: boolean
  /** The client holds a license, in force or not. */
  licensed: boolean
  /** On at this facility: licensed, in force, switched on, within the limit. */
  enabled: boolean
}

/** Reads the facility (which carries `features`) and the session (which carries the licenses); nothing extra is fetched. */
export function useFeature(facilityId: string | null, key: string): FeatureStanding {
  const me = useMe()
  const facility = useFacility(facilityId)
  return {
    isLoading: me.isPending || (facilityId !== null && facility.isPending),
    licensed: me.data?.features?.[key]?.licensed ?? false,
    enabled: facility.data?.features?.[key] ?? false,
  }
}
